import { useEffect } from "react";

const SKIPPED_PREFIXES = ["/api/", "/manus-storage/", "/__manus__/"];
const FILE_PATH = /\/[^/?#]+\.[a-z0-9]{1,12}(?:[?#]|$)/i;

/** Adds the preferred trailing slash to same-site navigational links only. */
export function normaliseInternalHref(value: string) {
  if (!value.startsWith("/") || value.startsWith("//") || SKIPPED_PREFIXES.some(prefix => value.startsWith(prefix))) return value;
  const [pathAndQuery, hash = ""] = value.split("#", 2);
  const [path, query = ""] = pathAndQuery.split("?", 2);
  if (path === "/" || path.endsWith("/") || FILE_PATH.test(path)) return value;
  return `${path}/${query ? `?${query}` : ""}${hash ? `#${hash}` : ""}`;
}

/**
 * Source copy still includes historical no-slash links. This normalises the
 * rendered anchor targets without touching APIs, assets or external links.
 */
export default function CanonicalLinkNormalizer() {
  useEffect(() => {
    const normalise = (anchor: HTMLAnchorElement) => {
      const href = anchor.getAttribute("href");
      if (!href) return;
      const normalised = normaliseInternalHref(href);
      if (normalised !== href) anchor.setAttribute("href", normalised);
    };
    const normaliseAll = (root: ParentNode = document) => root.querySelectorAll<HTMLAnchorElement>("a[href]").forEach(normalise);
    normaliseAll();

    const observer = new MutationObserver(records => {
      for (const record of records) {
        record.addedNodes.forEach(node => {
          if (node.nodeType === Node.ELEMENT_NODE) normaliseAll(node as Element);
        });
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return null;
}
